import { SearchPreferences } from './searchPreferences';
import {
  SortEnum,
  EditorialProductEnum,
  AllowedUseSearchFilterEnum,
  OfflineContentSearchFilterEnum,
  PhraseProcessingSearchFilterEnum,
} from '../searchParameters';
import { SRPLayoutEnum, RefinementPanelStateEnum } from '../srpLayout';

import type { SearchPreferencesDTO, SearchPreferencesScopeDTO } from './dto';

// Context filter values are comma-separated short codes. The code → value
// mappings must match the server-side table that writes the same stored form.
const ALLOWED_USE_CODES: Record<string, AllowedUseSearchFilterEnum> = {
  ed: AllowedUseSearchFilterEnum.EDITORIAL_USE_ONLY,
  av: AllowedUseSearchFilterEnum.AVAILABLE_FOR_ALL_USES,
  of: AllowedUseSearchFilterEnum.OFF,
};

const PHRASE_PROCESSING_CODES: Record<
  string,
  PhraseProcessingSearchFilterEnum
> = {
  sepc: PhraseProcessingSearchFilterEnum.EXCLUDE_NATURAL_LANGUAGE,
  ppd: PhraseProcessingSearchFilterEnum.DEFAULT,
};

const EDITORIAL_PRODUCT_CODES: Record<string, EditorialProductEnum> = {
  al: EditorialProductEnum.ALL,
  ne: EditorialProductEnum.NEWS,
  sp: EditorialProductEnum.SPORT,
  en: EditorialProductEnum.ENTERTAINMENT,
  ar: EditorialProductEnum.ARCHIVAL,
};

const OFFLINE_CONTENT_CODES: Record<string, OfflineContentSearchFilterEnum> = {
  exc: OfflineContentSearchFilterEnum.EXCLUSIVE,
  inc: OfflineContentSearchFilterEnum.INCLUDE,
};

const EXCLUDE_NUDITY_CODES: Record<string, boolean> = {
  t: true,
  f: false,
};

const IMAGE_LICENSE_CODES = ['rf', 'rm'];
const FILM_LICENSE_CODES = ['rf', 'rr'];

// The values the stored form can actually carry, derived from the code maps
// above rather than from the enums, so a validator built on this cannot drift
// from the encoder. The two differ: `AllowedUseSearchFilterEnum.ALL` and
// `OfflineContentSearchFilterEnum.EXCLUDE` are legal enum members with no
// short code, and `serializeScopes` drops them.
export const ENCODABLE_SCOPE_VALUES = {
  allowedUse: Object.values(ALLOWED_USE_CODES),
  phraseProcessing: Object.values(PHRASE_PROCESSING_CODES),
  editorialProducts: Object.values(EDITORIAL_PRODUCT_CODES),
  offlineContent: Object.values(OFFLINE_CONTENT_CODES),
  imageLicense: IMAGE_LICENSE_CODES,
  filmLicense: FILM_LICENSE_CODES,
} as const;

const shortCodes = (value?: string): string[] =>
  value ? value.split(',').filter(Boolean) : [];

const firstMatchOptional = <T>(
  codes: string[],
  codeMap: Record<string, T>
): T | undefined => {
  const match = codes.find((code) => code in codeMap);
  return match === undefined ? undefined : codeMap[match];
};

const licenses = (value: string | undefined, validCodes: string[]): string[] =>
  shortCodes(value).filter((code) => validCodes.includes(code));

const toEnum = <T extends string>(
  enumObject: Record<string, T>,
  value?: string
): T | undefined =>
  Object.values(enumObject).includes(value as T) ? (value as T) : undefined;

const toEnumOr = <T extends string>(
  enumObject: Record<string, T>,
  value: string | undefined,
  fallback: T
): T => toEnum(enumObject, value) ?? fallback;

const optionalString = (value?: string): string | undefined =>
  value || undefined;

const savedFilterEntries = (value?: string): string[] =>
  value ? value.split('&').filter(Boolean) : [];

// gateway_srp is written as '1'/'0' while the other flags use 'true'/'false'.
// An unrecognised or absent value means the flag was never written.
const toOptionalBoolean = (value?: string): boolean | undefined => {
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return undefined;
};

const toBoolean = (value?: string): boolean =>
  toOptionalBoolean(value) ?? false;

// --- encoding ---------------------------------------------------------------

// The short code for a value, from the same map the decoder reads. Inverting
// the map rather than declaring a second one keeps the two directions from
// drifting apart.
const codeFor = <T>(
  codeMap: Record<string, T>,
  value: T | undefined
): string | undefined =>
  value === undefined
    ? undefined
    : Object.keys(codeMap).find((code) => codeMap[code] === value);

// A context field is one comma-separated list of short codes. The order is
// fixed to match the server-side writer, so both produce a comparable form.
const joinCodes = (...codes: (string | undefined)[]): string =>
  codes.filter(Boolean).join(',');

export class SearchPreferencesSerializer {
  deserialize = (dto: SearchPreferencesDTO): SearchPreferences => {
    const creativeImage = shortCodes(dto.creative_image);
    const creativeFilm = shortCodes(dto.creative_film);
    const creativeImageFilm = shortCodes(dto.creative_image_film);

    return {
      searchDetails: optionalString(dto.search_details),
      refinementPanelState: toEnum(
        RefinementPanelStateEnum,
        dto.refinement_panel_state
      ),
      editorialSort: toEnumOr(SortEnum, dto.editorial_sort, SortEnum.BEST),
      editorialArchivalSort: toEnumOr(
        SortEnum,
        dto.editorial_archival_sort,
        SortEnum.BEST
      ),
      editorialEventSort: toEnumOr(
        SortEnum,
        dto.editorial_event_sort,
        SortEnum.BEST
      ),
      searchGridLayout: toEnumOr(
        SRPLayoutEnum,
        dto.search_grid_layout,
        SRPLayoutEnum.MOSAIC
      ),
      imageSearchGridLayout: toEnumOr(
        SRPLayoutEnum,
        dto.image_search_grid_layout,
        SRPLayoutEnum.MOSAIC
      ),
      filmSearchGridLayout: toEnumOr(
        SRPLayoutEnum,
        dto.film_search_grid_layout,
        SRPLayoutEnum.MOSAIC
      ),
      savedFilters: savedFilterEntries(dto.saved_filters),
      paAgreement: optionalString(dto.pa_agreement),
      hideCreativeBanner: toBoolean(dto.hide_creative_banner),
      hidePrestigeBanner: toBoolean(dto.hide_prestige_banner),
      hideAdobePluginBanner: toBoolean(dto.hide_adobe_plugin_banner),
      hideSingleSeatSubscriptionBanner: toBoolean(
        dto.hide_single_seat_subscription_banner
      ),
      creativeImage: {
        allowedUse: firstMatchOptional(creativeImage, ALLOWED_USE_CODES),
        phraseProcessing: firstMatchOptional(
          creativeImage,
          PHRASE_PROCESSING_CODES
        ),
        excludeNudity: firstMatchOptional(creativeImage, EXCLUDE_NUDITY_CODES),
      },
      editorialImage: {
        // Single, not a list. The storage format can carry several, but
        // nothing produces more than one: the
        // editorial nav sets a single product, and `editorialproducts` is a
        // single-valued ENUM in SEARCH_PARAMETERS_SCHEMA, so a second could
        // never reach a search anyway.
        editorialProducts: firstMatchOptional(
          shortCodes(dto.editorial_image),
          EDITORIAL_PRODUCT_CODES
        ),
      },
      editorialFilm: {
        offlineContent: firstMatchOptional(
          shortCodes(dto.editorial_film),
          OFFLINE_CONTENT_CODES
        ),
      },
      mixedImage: {
        license: licenses(dto.mixed_image, IMAGE_LICENSE_CODES),
      },
      creativeFilm: {
        license: licenses(dto.creative_film, FILM_LICENSE_CODES),
        phraseProcessing: firstMatchOptional(
          creativeFilm,
          PHRASE_PROCESSING_CODES
        ),
      },
      mixedFilm: {
        license: licenses(dto.mixed_film, FILM_LICENSE_CODES),
      },
      analogFilm: {
        license: licenses(dto.analog_film, FILM_LICENSE_CODES),
      },
      creativeImageFilm: {
        // Single, matching creativeImage.allowedUse. Multi was never writable
        // here: CookieDescriptor#find_value_match substring-tests the value,
        // and 'editorialuseonly' / 'availableforalluses' / 'off' are not
        // substrings of one another, so only one can ever match.
        allowedUse: firstMatchOptional(creativeImageFilm, ALLOWED_USE_CODES),
        phraseProcessing: firstMatchOptional(
          creativeImageFilm,
          PHRASE_PROCESSING_CODES
        ),
        excludeNudity: firstMatchOptional(
          creativeImageFilm,
          EXCLUDE_NUDITY_CODES
        ),
      },
      groupByEvent: toOptionalBoolean(dto.group_by_event),
      gatewaySrp: toBoolean(dto.gateway_srp),
      suppressEnhancedPhraseCorrection: toBoolean(
        dto.suppress_enhanced_phrase_correction
      ),
    };
  };

  /**
   * The per-scope fields of the stored DTO for these preferences.
   *
   * **Scopes only.** The global fields — sort, layout, banner dismissals,
   * saved filters — are deliberately not produced here, so a caller writing
   * the result back cannot clobber preferences it never intended to touch.
   * Each scope field is a whole-value replace in the stored form, so whatever is
   * handed in is the complete state of that scope: callers must merge onto a
   * freshly deserialized value rather than onto a partial.
   *
   * The exact inverse of the scope half of `deserialize`, reading the same
   * code maps so the two cannot drift.
   */
  serializeScopes = (
    preferences: SearchPreferences
  ): SearchPreferencesScopeDTO => ({
    creative_image: joinCodes(
      codeFor(ALLOWED_USE_CODES, preferences.creativeImage.allowedUse),
      codeFor(
        PHRASE_PROCESSING_CODES,
        preferences.creativeImage.phraseProcessing
      ),
      codeFor(EXCLUDE_NUDITY_CODES, preferences.creativeImage.excludeNudity)
    ),
    editorial_image: joinCodes(
      codeFor(
        EDITORIAL_PRODUCT_CODES,
        preferences.editorialImage.editorialProducts
      )
    ),
    mixed_image: preferences.mixedImage.license.join(','),
    creative_film: joinCodes(
      preferences.creativeFilm.license.join(',') || undefined,
      codeFor(
        PHRASE_PROCESSING_CODES,
        preferences.creativeFilm.phraseProcessing
      )
    ),
    editorial_film: joinCodes(
      codeFor(OFFLINE_CONTENT_CODES, preferences.editorialFilm.offlineContent)
    ),
    mixed_film: preferences.mixedFilm.license.join(','),
    analog_film: preferences.analogFilm.license.join(','),
    creative_image_film: joinCodes(
      codeFor(ALLOWED_USE_CODES, preferences.creativeImageFilm.allowedUse),
      codeFor(
        PHRASE_PROCESSING_CODES,
        preferences.creativeImageFilm.phraseProcessing
      ),
      codeFor(EXCLUDE_NUDITY_CODES, preferences.creativeImageFilm.excludeNudity)
    ),
  });
}
